import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from 'api/Api';
import { Message } from 'api/MessagesApi';

export const adminAccessMessagesApi = createApi({
  baseQuery: baseQueryWithReAuth,
  reducerPath: 'adminAccessMessagesApi',
  tagTypes: ['AdminAccessMessages', 'AdminAccessMessageFiles'],
  endpoints: (build) => ({
    getAllMessages: build.query<{ list: Message[]; total: number }, { id: string; limit?: number; offset?: number }>({
      query: ({ id, ...params }) => ({ url: `/admin-access/${id}/messages`, params }),
      transformResponse: (response: { data: [Message[], number] }) => ({
        list: response.data[0],
        total: response.data[1]
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.list.map(({ id }) => ({
                type: 'AdminAccessMessages' as const,
                id
              })),
              { type: 'AdminAccessMessages', id: 'LIST' }
            ]
          : [{ type: 'AdminAccessMessages', id: 'LIST' }]
    }),
    patchAdminAccessMessage: build.mutation<
      void,
      {
        id: string;
        messageId: string;
        deliveryYear?: number | null;
        deliveryMonth?: number | null;
        deliveryDay?: number | null;
        eventType?: string;
        contacts?: { id?: string; name: string; email: string; phone: string }[];
      }
    >({
      query: ({ id, messageId, ...body }) => ({
        url: `/admin-access/${id}/messages/${messageId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (result, error, { messageId }) => [
        { type: 'AdminAccessMessages', id: messageId },
        { type: 'AdminAccessMessages', id: 'LIST' }
      ]
    }),
    getOneAdminAccessMessage: build.query<Message, { id: string; messageId: string }>({
      query: ({ id, messageId }) => ({ url: `/admin-access/${id}/messages/${messageId}` }),
      transformResponse: (response: { data: Message }) => response.data,
      providesTags: (result, error, { messageId }) => [{ type: 'AdminAccessMessages', id: messageId }]
    }),
    getOneAdminAccessFileMessage: build.query<{ signedUrl: string }, { id: string; messageId: string }>({
      query: ({ id, messageId }) => ({ url: `/admin-access/${id}/messages/${messageId}/file` }),
      transformResponse: (response: { data: { signedUrl: string } }) => response.data,
      providesTags: (result, error, { messageId }) => [{ type: 'AdminAccessMessageFiles', id: messageId }]
    })
  })
});

export const {
  useGetAllMessagesQuery,
  usePatchAdminAccessMessageMutation,
  useGetOneAdminAccessMessageQuery,
  useGetOneAdminAccessFileMessageQuery
} = adminAccessMessagesApi;
